import { memo, useEffect, useMemo, useState } from 'react';
import { Text, View, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { THEME, COLOR } from '../../../constants/styles';
import { Navigation, Container, FormFooter, FormInput, Utils } from '../../components';
import { REQUESTING, SUCCESS } from '../../../actions/statusTypes';
import { resetStatus, sendAppointmentRequestSms } from './slice';

const MESSAGE_MAX_LENGTH = 320;

const getSmsInfoDetails = (request, event) => {
  if (request) return request.details;

  if (!event) return null;

  return {
    full_name: event.full_name || event.caseName || null,
    phone: event.phone || null,
  };
};

const getSmsIdentifiers = (request, event) => {
  if (request?.appointmentRequestId) {
    return { appointmentRequestId: request.appointmentRequestId };
  }

  if (event?.eventId) {
    return { eventId: event.eventId };
  }

  return null;
};

const TextRow = ({ label, value }) => {
  return (
    <View style={styles.textRow}>
      <Text style={styles.textRowLabel}>{label}: </Text>
      <Text style={styles.textRowValue}>{value || 'N/A'}</Text>
    </View>
  );
};

const AppointmentRequestSms = memo(({ request, event, employerId }) => {
  const dispatch = useDispatch();
  const sendSMSError = useSelector((state) => state.screens.scheduler.sendSMSError);
  const sendSMSStatus = useSelector((state) => state.screens.scheduler.sendSMSStatus);
  const [message, setMessage] = useState('');

  useEffect(() => {
    dispatch(resetStatus());
    return () => {
      dispatch(resetStatus());
    };
  }, [dispatch]);

  const details = useMemo(() => getSmsInfoDetails(request, event), [request, event]);
  const identifiers = useMemo(() => getSmsIdentifiers(request, event), [request, event]);
  const sanitizedMessage = Utils.validate
    .sanitizeFreeText(message)
    .trim()
    .substring(0, MESSAGE_MAX_LENGTH);
  const isSubmitting = sendSMSStatus === REQUESTING;
  const isSuccess = sendSMSStatus === SUCCESS;
  const hasPreviewMessage = Boolean(sanitizedMessage);
  const isMissingRequestData = !employerId || !identifiers;
  const isSendDisabled = isMissingRequestData || !hasPreviewMessage || isSubmitting || isSuccess;

  const handleCancel = () => {
    Navigation.dismissAllModals();
  };

  const handleSend = () => {
    if (isSendDisabled) return;
    dispatch(sendAppointmentRequestSms({ employerId, identifiers, message: sanitizedMessage }));
  };

  const footer = (
    <FormFooter
      onCancel={handleCancel}
      cancelLabel={isSuccess ? 'Done' : 'Cancel'}
      saveLabel={'Send'}
      onSave={isSuccess ? null : handleSend}
      saveDisabled={isSendDisabled}
      forceHorizontal={true}
    />
  );

  return (
    <Container
      style={THEME.container}
      renderActivityOverlay={isSubmitting}
      errorMessage={sendSMSError?.message}
      successMessage={isSuccess ? 'Text message sent successfully.' : null}
      scrollView={true}
      stickyFooter={footer}
    >
      <View style={styles.contents}>
        <Text style={styles.info}>
          A text message will be sent to the service member with the sanitized message shown in the
          preview.
        </Text>
        <View style={styles.topSection}>
          <TextRow label="Patient Name" value={details?.full_name} />
          <TextRow label="Phone" value={details?.phone} />
        </View>

        <FormInput
          value={message}
          label="Message"
          required={true}
          placeholder="Type the SMS message to send to the service member"
          sanitizer={Utils.validate.sanitizeFreeText}
          onChangeText={setMessage}
          controlled={true}
          autoCorrect={false}
          autoCapitalize="sentences"
          multiline={true}
          numberOfLines={4}
          maxLength={MESSAGE_MAX_LENGTH}
        />

        <Text
          style={styles.helperText}
        >{`${sanitizedMessage.length}/${MESSAGE_MAX_LENGTH} characters`}</Text>

        {hasPreviewMessage && (
          <>
            <Text style={THEME.labelText}>MESSAGE PREVIEW</Text>
            <View style={styles.previewBox}>
              <Text style={THEME.textRegular}>{sanitizedMessage}</Text>
            </View>
          </>
        )}
      </View>
    </Container>
  );
});

export default AppointmentRequestSms;

const styles = StyleSheet.create({
  contents: {
    padding: 25,
    zIndex: 100,
  },
  info: {
    ...THEME.textRegular,
    marginBottom: 20,
    // textAlign: 'justify',
  },
  topSection: {
    // marginBottom: 10,
  },
  textRow: {
    flexDirection: 'row',
    marginBottom: 10,
    alignItems: 'center',
  },
  textRowLabel: {
    ...THEME.labelText,
    width: 110,
  },
  textRowValue: {
    ...THEME.textRegular,
    flex: 1,
  },
  helperText: {
    ...THEME.textRegular,
    color: COLOR.textLight,
    marginTop: -4,
    marginBottom: 12,
    fontSize: 14,
    alignSelf: 'flex-end',
  },
  previewBox: {
    marginTop: 8,
    marginBottom: 12,
    padding: 10,
    backgroundColor: COLOR.altContrast,
    borderRadius: 4,
  },
});
